import { useId, useState } from "react";
import type { GroupView } from "@starsach/shared-types";
import { useAccess, useCreateGroup, useDeleteGroup, useGroups, useOrganisationUsers, useRoles, useUpdateGroup } from "../../api/hooks";
import { useAuth } from "../../auth/AuthProvider";
import { useStepUp } from "../../auth/StepUpProvider";
import { useEnvironmentName, useVisibleEnvironments } from "../../components/Environment";
import { Hint } from "../../components/Hint";
import { NotHeld } from "../../components/NotHeld";
import { Card, DataTable, Dialog, EmptyState, ErrorState, LoadingState, MutationError, RelativeTime, btnPrimary, btnSecondary, inputClass } from "../../components/ui";
import { RolePills, orderRoles } from "./parts";

type Draft = { groupId?: string; name: string; roleIds: Set<string>; memberUserIds: Set<string>; environmentIds: Set<string> };

/** Groups (ADR-0017 section 2): a named set of roles, its members, and the environments the roles apply on. */
export function GroupsSection({ environmentId }: { environmentId: string }) {
  const q = useGroups();
  const roles = useRoles();
  const access = useAccess();
  const create = useCreateGroup();
  const update = useUpdateGroup();
  const remove = useDeleteGroup();
  const { ensureFresh } = useStepUp();
  const envName = useEnvironmentName();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [deleting, setDeleting] = useState<GroupView | null>(null);
  const canCreate = access.can(environmentId, "roles:create");
  const canEdit = access.can(environmentId, "roles:edit");
  const canDelete = access.can(environmentId, "roles:delete");

  if (q.isPending) return <LoadingState label="Loading groups" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;

  const save = (d: Draft) => {
    const body = { name: d.name.trim(), roleIds: [...d.roleIds], memberUserIds: [...d.memberUserIds], environmentIds: [...d.environmentIds] };
    void ensureFresh().then(() => {
      if (d.groupId) update.mutate({ groupId: d.groupId, ...body }, { onSuccess: () => setDraft(null) });
      else create.mutate(body, { onSuccess: () => setDraft(null) });
    }, () => undefined);
  };

  return (
    <div className="space-y-4">
      <Card title={<>Groups<Hint id="access.groups" label="what a group is" /></>}>
        <p className="mb-3 text-sm text-neutral-600 dark:text-neutral-400">
          A group gives several people the same roles on the same environments at once. A member holds the group's roles
          there in addition to any granted to them directly.
        </p>
        <MutationError of={remove} className="mb-3" />
        <DataTable
          caption="Groups in your organization"
          rows={q.data.groups}
          rowKey={(g) => g.groupId}
          sortBy={(g) => g.name}
          empty={<EmptyState title="No groups yet" hint="A group is worth making when several people should hold the same roles on the same environments." />}
          columns={[
            { key: "name", header: "Group", render: (g) => <span className="font-medium">{g.name}</span> },
            { key: "roles", header: "Roles", render: (g) => <RolePills roleIds={g.roleIds} roles={roles.data?.roles} /> },
            { key: "members", header: "Members", render: (g) => (g.members.length === 0 ? <span className="text-sm text-neutral-500">none</span> : <span className="text-sm">{g.members.map((m) => m.email ?? m.userId).join(", ")}</span>) },
            { key: "envs", header: "Environments", render: (g) => (g.environmentIds.length === 0 ? <span className="text-sm text-neutral-500">none</span> : <span className="text-sm">{g.environmentIds.map(envName).join(", ")}</span>) },
            { key: "updated", header: "Changed", render: (g) => <RelativeTime iso={g.updatedAt} /> },
            {
              key: "actions",
              header: "",
              render: (g) => (
                <span className="flex gap-2">
                  {canEdit && (
                    <button type="button" className="text-sm underline" onClick={() => setDraft({ groupId: g.groupId, name: g.name, roleIds: new Set(g.roleIds), memberUserIds: new Set(g.members.map((m) => m.userId)), environmentIds: new Set(g.environmentIds) })}>
                      Edit
                    </button>
                  )}
                  {canDelete && (
                    <button type="button" className="text-sm text-red-700 underline dark:text-red-400" onClick={() => setDeleting(g)}>Delete</button>
                  )}
                </span>
              ),
            },
          ]}
        />
        <div className="mt-4">
          {canCreate ? (
            <button type="button" className={btnPrimary} onClick={() => setDraft({ name: "", roleIds: new Set(), memberUserIds: new Set(), environmentIds: new Set([environmentId]) })}>
              New group
            </button>
          ) : (
            <NotHeld permission="roles:create" />
          )}
        </div>
      </Card>
      {draft && <GroupDialog draft={draft} onChange={setDraft} onSave={save} onClose={() => setDraft(null)} pending={create.isPending || update.isPending} error={create.isError ? create.error : update.isError ? update.error : undefined} />}
      {deleting && (
        <Dialog title={`Delete ${deleting.name}?`} onClose={() => setDeleting(null)}>
          <p className="text-sm">
            {deleting.members.length === 0 ? "The group has no members." : `${deleting.members.length} member${deleting.members.length === 1 ? "" : "s"} lose${deleting.members.length === 1 ? "s" : ""} the group's roles at once; roles granted to them directly stay.`}
          </p>
          <MutationError of={remove} className="mt-3" />
          <div className="mt-4 flex gap-2">
            <button type="button" className={btnPrimary} disabled={remove.isPending} onClick={() => void ensureFresh().then(() => remove.mutate({ groupId: deleting.groupId }, { onSuccess: () => setDeleting(null) }), () => undefined)}>
              {remove.isPending ? "Deleting…" : "Delete"}
            </button>
            <button type="button" className={btnSecondary} onClick={() => setDeleting(null)}>Cancel</button>
          </div>
        </Dialog>
      )}
    </div>
  );
}

function GroupDialog({ draft, onChange, onSave, onClose, pending, error }: { draft: Draft; onChange: (d: Draft) => void; onSave: (d: Draft) => void; onClose: () => void; pending: boolean; error: unknown }) {
  const { me } = useAuth();
  const roles = useRoles();
  const envs = useVisibleEnvironments();
  // The organization's people are a customer's list (ADR-0013); staff editing a group name members by id.
  const users = useOrganisationUsers(me?.role === "customer" || Boolean(me?.viewAs));
  const id = useId();
  const toggle = (set: Set<string>, value: string, on: boolean) => {
    const next = new Set(set);
    if (on) next.add(value);
    else next.delete(value);
    return next;
  };
  const people = (users.data?.users ?? []).filter((u) => !u.removedAt);
  return (
    <Dialog title={draft.groupId ? `Edit ${draft.name}` : "New group"} onClose={onClose} wide>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          onSave(draft);
        }}
      >
        <label htmlFor={`${id}-name`} className="block text-sm">
          <span className="font-medium">Name</span>
          <input id={`${id}-name`} className={inputClass} value={draft.name} maxLength={60} required onChange={(e) => onChange({ ...draft, name: e.target.value })} />
        </label>
        <div className="grid gap-4 md:grid-cols-3">
          <fieldset>
            <legend className="text-sm font-medium">Roles</legend>
            {roles.isError && <ErrorState error={roles.error} />}
            <ul className="mt-2 space-y-1">
              {orderRoles(roles.data?.roles ?? []).map((r) => (
                <li key={r.roleId}>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={draft.roleIds.has(r.roleId)} onChange={(e) => onChange({ ...draft, roleIds: toggle(draft.roleIds, r.roleId, e.target.checked) })} />
                    {r.name}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          <fieldset>
            <legend className="text-sm font-medium">Members</legend>
            {users.isError && <ErrorState error={users.error} />}
            {people.length === 0 && !users.isPending && <p className="mt-2 text-xs text-neutral-500">{users.data ? "Nobody to add yet." : "Members are chosen from the organization's people; as staff, edit from their portal."}</p>}
            <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto">
              {people.map((u) => (
                <li key={u.userId}>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={draft.memberUserIds.has(u.userId)} onChange={(e) => onChange({ ...draft, memberUserIds: toggle(draft.memberUserIds, u.userId, e.target.checked) })} />
                    {u.displayName ? `${u.displayName} (${u.email ?? u.userId})` : (u.email ?? u.userId)}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          <fieldset>
            <legend className="text-sm font-medium">Environments</legend>
            {envs.isError && <ErrorState error={envs.error} />}
            <ul className="mt-2 space-y-1">
              {(envs.data?.customers ?? []).map((e) => (
                <li key={e.environmentId}>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={draft.environmentIds.has(e.environmentId)} onChange={(ev) => onChange({ ...draft, environmentIds: toggle(draft.environmentIds, e.environmentId, ev.target.checked) })} />
                    {e.friendlyName}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        </div>
        {error !== undefined && <ErrorState error={error} lead="Couldn’t do that." />}
        <div className="flex gap-2">
          <button type="submit" className={btnPrimary} disabled={pending || !draft.name.trim() || draft.roleIds.size === 0}>
            {pending ? "Saving…" : draft.groupId ? "Save group" : "Create group"}
          </button>
          <button type="button" className={btnSecondary} onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Dialog>
  );
}
